import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

export interface OrderCo2ePerLogoDisplay {
  logoId: number;
  logoName: string;
  logoSerial?: string;
  amount: number;
  contribution: number;
}

export interface Props {
  total: number;
  perLogo: OrderCo2ePerLogoDisplay[];
}

function formatValue(x: number): string {
  if (x >= 1) return x.toFixed(2);
  return x.toExponential(2);
}

export function OrderCo2eEstimate({ total, perLogo }: Props) {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const canExpand = perLogo.length > 0;
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!expanded) return;
    function handleClickOutside(e: MouseEvent) {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        setExpanded(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [expanded]);

  return (
    <div className={"relative"} ref={containerRef}>
      <button
        type="button"
        onClick={() => canExpand && setExpanded((v) => !v)}
        disabled={!canExpand}
        aria-expanded={expanded}
        className={
          "inline-flex items-center gap-1.5 rounded-full bg-white border border-emerald-300 px-2.5 py-1 " +
          "text-emerald-800 shadow-sm " +
          (canExpand
            ? "cursor-pointer hover:bg-emerald-50 transition-colors"
            : "cursor-default")
        }
      >
        <LeafIcon />
        <span className={"tabular-nums text-xs font-medium"}>
          {formatValue(total)}
          <span className={"ml-1 text-emerald-600 font-normal"}>
            {t("order_confirmation.co2e.unit")}
          </span>
        </span>
      </button>

      {canExpand && expanded && (
        <div
          className={
            "absolute right-0 top-full mt-2 z-20 min-w-[260px] " +
            "rounded-lg border border-emerald-200 bg-white shadow-lg p-3"
          }
          role="dialog"
        >
          <div className={"flex items-start justify-between mb-2"}>
            <div>
              <div
                className={
                  "text-[10px] uppercase tracking-[0.14em] text-emerald-800/70"
                }
              >
                {t("order_confirmation.co2e.label")}
              </div>
              <div
                className={
                  "tabular-nums text-emerald-900 text-base font-semibold leading-tight"
                }
              >
                {formatValue(total)}
                <span
                  className={
                    "ml-1 text-emerald-700/80 text-xs font-normal"
                  }
                >
                  {t("order_confirmation.co2e.unit")}
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={() => setExpanded(false)}
              aria-label={t("close")}
              className={
                "text-stone-400 hover:text-stone-700 transition-colors leading-none -mt-1 -mr-1 p-1"
              }
            >
              <CloseIcon />
            </button>
          </div>

          <div className={"border-t border-emerald-200/70 pt-2"}>
            <table className={"w-full text-xs border-collapse"}>
              <thead>
                <tr
                  className={
                    "text-[10px] uppercase tracking-[0.12em] text-emerald-800/70"
                  }
                >
                  <th
                    className={"text-left font-medium pb-1.5 pr-3"}
                  >
                    {t("order_confirmation.co2e.col_logo")}
                  </th>
                  <th
                    className={"text-right font-medium pb-1.5 pr-3"}
                  >
                    {t("order_confirmation.co2e.col_qty")}
                  </th>
                  <th className={"text-right font-medium pb-1.5"}>
                    {t("order_confirmation.co2e.col_co2e")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {perLogo.map((line) => (
                  <tr key={line.logoId} className={"align-baseline"}>
                    <td
                      className={
                        "text-stone-700 py-0.5 pr-3 max-w-[180px] truncate"
                      }
                      title={
                        line.logoSerial
                          ? `${line.logoName} (${line.logoSerial})`
                          : line.logoName
                      }
                    >
                      {line.logoName}
                      {line.logoSerial && (
                        <span className={"text-stone-400"}>
                          {" "}
                          ({line.logoSerial})
                        </span>
                      )}
                    </td>
                    <td
                      className={
                        "tabular-nums text-stone-500 text-right py-0.5 pr-3"
                      }
                    >
                      {line.amount}
                    </td>
                    <td
                      className={
                        "tabular-nums text-emerald-900 text-right py-0.5"
                      }
                    >
                      {formatValue(line.contribution)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div
            className={
              "mt-3 pt-2 border-t border-emerald-200/70 text-[10px] leading-snug text-stone-500 max-w-[260px]"
            }
          >
            <p>{t("orderConfirmation.co2eDisclaimer")}</p>
            <p className={"mt-1 italic"}>
              {t("orderConfirmation.co2eAppliesTo")}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function LeafIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 16 16"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={"text-emerald-600 shrink-0"}
      aria-hidden
    >
      <path
        d="M13.5 2.5C8 3 4 6.5 4 11C4 12.2 4.4 13 4.9 13.5C5.4 14 6.3 14.4 7.5 14.4C11.5 14.4 13.5 11 13.5 2.5Z"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinejoin="round"
      />
      <path
        d="M5 13.5L2.5 16"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeLinecap="round"
      />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      width="12"
      height="12"
      viewBox="0 0 12 12"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden
    >
      <path
        d="M3 3L9 9M9 3L3 9"
        stroke="currentColor"
        strokeWidth="1.3"
        strokeLinecap="round"
      />
    </svg>
  );
}
